import type { PatientRow } from '../../../../services/patientsV2';
import type { StatusCatalog } from '../../../../services/statusCatalog';
import type { PriorityLevel } from '../../../work-queue/priority/priority';
import { metaFor } from '../../../work-queue/versionedStatusMeta.ts';

// Row shape the Patients tab's table renders - joins the raw patient list
// row against the workflow taxonomy so the table never has to reach for two
// separate lookups per cell.
export interface PracticePatientRow {
    id: string;
    name: string;
    profileThumbnailUrl: string | null;
    // Catalog display string (code + label) for the internal step - never
    // null unless the patient itself has no internal status recorded at all.
    internalStepLabel: string | null;
    externalStep: string | null;
    // Null when internalCaseStatus is missing/unmatched - the table renders
    // an em-dash rather than guessing a phase or team.
    phase: string | null;
    team: string | null;
    // Always present - unlike phase/team there's no unmatched/missing case to
    // handle here (see PatientRow's comment).
    priority: PriorityLevel;
}

/**
 * Resolves a patient's internalCaseStatus against the workflow taxonomy.
 * Legacy/retired statuses genuinely have no catalog entry - that miss is
 * handled honestly: the catalog's own fallback still renders a display
 * string (never blank), but phase/team fall back to null so the table can
 * render an explicit em-dash instead of a guess.
 */
export function joinPatientWithStatus(
    patient: PatientRow,
    catalog: StatusCatalog
): PracticePatientRow {
    const matched = metaFor(
        patient.internalCaseStatus ? catalog.meta(patient.internalCaseStatus) : undefined,
        patient.version
    );

    return {
        id: patient.id,
        name: patient.name,
        profileThumbnailUrl: patient.profileThumbnailUrl,
        internalStepLabel: patient.internalCaseStatus
            ? catalog.display(patient.internalCaseStatus)
            : null,
        externalStep: patient.caseStatus,
        phase: matched?.phase ?? null,
        team: matched?.team ?? null,
        priority: patient.priority
    };
}
